import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useStatsigClient } from "@statsig/react-bindings";

import { HOMEPAGE_FEATURED } from "../../../GraphQL/homepageFeatured";
import { IFeaturedItem } from "./@types";
import { ModalType } from "../../../@types";
import {
  filterPromotionsByExperiment,
  shouldShowExperimentPromotion,
} from "MVVM/Utilities/filterPromotions";

interface IPromotion {
  default: boolean;
  image: string;
  name: string;
  tag: string;
  link: string;
  buttonCopy?: string;
  thumbnail?: string;
  buttonAction?: ModalType;
  experimentName?: string;
  external?: boolean;
  title: string;
  clip?: {
    shareId: string;
  };
}

const FeaturedViewModel = () => {
  const { data, error, loading } = useQuery(HOMEPAGE_FEATURED, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const [slideCount, setSlideCount] = useState(0);
  const { getExperiment } = useStatsigClient();

  const featuredContent = useMemo(() => {
    if (!data) return [];

    const featuredItems: IFeaturedItem[] = [];
    const { cotd, motw, promotions, top5 } = data;
    const activePromotions = promotions.activeCarouselPromos.filter(
      (promotion: IPromotion) => !promotion.default,
    );

    /* If we have non-default promos, those are "active" and we only show those
     Otherwise, we show the rest as defaults  */

    const featuredPromotions = activePromotions
      ? activePromotions
      : promotions.activeCarouselPromos;

    if (top5?.nodes?.[0]) {
      const { user, ...nodeData } = top5.nodes[0];
      featuredItems.push({
        ...nodeData,
        ...(user && {
          avatar: user.avatarUrl,
          username: user.username,
          userId: user.internalId,
        }),
        link: `/clip/${nodeData.shareId}`,
        type: "top5",
        tag: "WATCH",
        buttonCopy: "Watch",
      });
    }

    if (cotd?.nodes?.[0]) {
      const { user, ...nodeData } = cotd.nodes[0];
      featuredItems.push({
        ...nodeData,
        ...(user && {
          avatar: user.avatarUrl,
          username: user.username,
          userId: user.internalId,
        }),
        link: `/clip/${nodeData.shareId}`,
        type: "cotd",
        tag: "WATCH",
        buttonCopy: "Watch",
      });
    }

    if (motw?.nodes?.[0]) {
      const { user, ...nodeData } = motw.nodes[0];
      featuredItems.push({
        ...nodeData,
        ...(user && {
          avatar: user.avatarUrl,
          username: user.username,
          userId: user.internalId,
        }),
        link: `/clip/${nodeData.shareId}`,
        type: "motw",
        tag: "WATCH",
        buttonCopy: "Watch",
      });
    }

    const filteredFeaturedPromotions = filterPromotionsByExperiment(
      featuredPromotions,
      (experimentName) =>
        shouldShowExperimentPromotion(experimentName, getExperiment),
      getExperiment,
    );

    if (filteredFeaturedPromotions.length) {
      filteredFeaturedPromotions.forEach((promotion: IPromotion) => {
        featuredItems.push({
          title: promotion.title,
          url: promotion.image,
          type: "promotion",
          link: promotion.link,
          tag: promotion.tag,
          buttonCopy: promotion.buttonCopy,
          external: promotion.external,
          buttonAction: promotion.buttonAction,
          shareId: promotion.clip?.shareId,
          thumbnailUrl: promotion.thumbnail,
          promotionName: promotion.name,
        });
      });
    }

    return featuredItems;
  }, [data, getExperiment]);

  useEffect(
    () => setSlideCount(featuredContent.length),
    [featuredContent.length],
  );

  return { featuredContent, error, loading, slideCount };
};

export { FeaturedViewModel };
